import getSymbolFromCurrency from 'currency-symbol-map';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { Keyboard, StyleSheet, View, } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming, } from 'react-native-reanimated';
import Logo from 'design-system/Logo';
import Text from 'design-system/Text';
import Amount from 'design-system/Amount';
import { Button, CollapsableContainer, LineSeparator, rem, Spacer, } from 'design-system';
import IconCircle from 'design-system/icons/IconCircle';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { MoneyTextInput } from 'utils/packages/moneyInput';
import { useMoneyInputText } from 'hooks/useMoneyInputText';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import { formatAmountInput } from 'utils/formatting';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import IconTransactionCategory from 'features/transactions/icons/IconTransactionCategory';
import { blur, focus } from 'hooks/useAutoFocusInput';
import { useGetIsKeyboardOpen } from 'hooks';
import { isAndroid, isWeb } from '../../../constants';
import { useEditBudgetsCategoryModal } from '../hooks/useEditBudgetsCategoryModal';
const BudgetCategoryEditRow = ({ budget, onChange, hasAverages, withInitialFocus, modalShownRef, onModalShown, subcategories, initialFocusCategoryId, isOverParentBudget: isOverParentBudgetProp, setOverParentError: setOverParentErrorProp, scrollRef, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { isUnlockedForSpace, onPressUpgrade } = useShouldShowUpgrade({
        benefitId: 'rollingBudgets',
    });
    const [IsFocused, setIsFocused] = React.useState(false);
    const subcategoriesTotalLimit = useMemo(() => subcategories?.reduce((acc, subcategory) => acc + (subcategory.totalLimit || 0), 0), [subcategories]);
    const hasSubcategories = !!(subcategories && subcategories.length);
    const isParent = !budget.parentCategoryId && hasSubcategories;
    const [overParentError, setOverParentError] = useState((subcategoriesTotalLimit ?? 0) > (budget.totalLimit ?? 0));
    const isOverParentBudget = useMemo(() => (subcategoriesTotalLimit ?? 0) > (budget.totalLimit ?? 0) ||
        isOverParentBudgetProp, [budget.totalLimit, isOverParentBudgetProp, subcategoriesTotalLimit]);
    const assureInputVisible = useCallback((timeout) => {
        const assureFocusedInputVisible = scrollRef?.current?.getNativeScrollRef?.()?.assureFocusedInputVisible;
        if (assureFocusedInputVisible) {
            setTimeout(() => assureFocusedInputVisible(), timeout);
        }
    }, [scrollRef]);
    // Keep the parent's error state in sync with the live total as subcategories
    // are typed, instead of only updating on focus/blur of a child row.
    useEffect(() => {
        (setOverParentErrorProp ?? setOverParentError)(!!isOverParentBudget);
        if (isOverParentBudget) {
            assureInputVisible(500);
        }
    }, [assureInputVisible, isOverParentBudget, setOverParentErrorProp]);
    const currency = useMemo(() => getSymbolFromCurrency(budget.currency), [budget.currency]);
    const valueFromBudget = useMemo(() => (budget.merchant ? budget.budgetLimit : budget.totalLimit), [budget]);
    const [value, setValue] = useState(() => typeof valueFromBudget === 'number' && valueFromBudget > 0
        ? valueFromBudget
        : null);
    // Formatted-string mirror to size the input (see `marginRight`).
    const [amount, setAmount] = useState(() => typeof valueFromBudget === 'number' && valueFromBudget > 0
        ? formatAmountInput(String(valueFromBudget.toFixed(valueFromBudget % 1 !== 0 ? 2 : 0)), currency).value
        : '');
    useEffect(() => {
        const nextValue = typeof valueFromBudget === 'number' && valueFromBudget > 0
            ? valueFromBudget
            : null;
        if (nextValue === value) {
            return;
        }
        setValue(nextValue);
        setAmount(nextValue === null
            ? ''
            : formatAmountInput(String(nextValue.toFixed(nextValue % 1 !== 0 ? 2 : 0)), currency).value);
        // Do not include value in the deps array
    }, [currency, valueFromBudget]);
    const textInputMaskRef = useRef(null);
    const didFocusRef = useRef(false);
    useEffect(() => {
        if (!didFocusRef.current &&
            (withInitialFocus ||
                ('categoryId' in budget &&
                    initialFocusCategoryId === budget.categoryId))) {
            const timeout = setTimeout(() => {
                textInputMaskRef.current?.focus();
                didFocusRef.current = true;
            }, 600);
            return () => {
                clearTimeout(timeout);
            };
        }
        return () => { };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFocusCategoryId, withInitialFocus]);
    const onChangeValue = useCallback((newValue) => {
        setValue(newValue);
        onChange(budget.key || '', newValue ?? 0);
    }, [budget.key, onChange]);
    const [text, onChangeMoneyText] = useMoneyInputText(value, onChangeValue);
    const onChangeText = useCallback((formatted, extracted) => {
        setAmount(formatted);
        onChangeMoneyText(formatted, extracted);
    }, [onChangeMoneyText]);
    const renderAmountRow = useMemo(() => {
        if (budget.rollingAccumulatedLimit) {
            return (<View>
          <Text TextThin-14 Secondary>
            {`Default `}
            <Amount NumbersThin-14 Secondary value={{
                    value: budget.baseLimit || 0,
                    currency: budget?.currency,
                }}/>
          </Text>
          {!isUnlockedForSpace ? <Spacer h={4}/> : null}
          <Text TextThin-14 color={budget.rollingAccumulatedLimit >= 0
                    ? colors.status.positive
                    : colors.status.negative} {...(!isUnlockedForSpace ? { Gray: true } : {})}>
            {`Rolled `}
            {!isUnlockedForSpace ? (<Button title="Upgrade" brandReversed square cards extra small hug onPress={onPressUpgrade}/>) : (<Amount Numbers-14 showSign color={budget?.rollingAccumulatedLimit >= 0
                        ? colors.status.positive
                        : colors.status.negative} value={{
                        value: budget.rollingAccumulatedLimit,
                        currency: budget?.currency,
                    }}/>)}
          </Text>
        </View>);
        }
        if (hasAverages) {
            return (<Text TextThin-14 Gray>
          {`Avg `}
          <Amount NumbersThin-14 Secondary value={{
                    value: budget.previousPeriodAverage || 0,
                    currency: budget?.currency,
                }}/>
        </Text>);
        }
        return null;
    }, [
        budget.baseLimit,
        budget?.currency,
        budget.previousPeriodAverage,
        budget.rollingAccumulatedLimit,
        colors.status.negative,
        colors.status.positive,
        hasAverages,
        isUnlockedForSpace,
        onPressUpgrade,
    ]);
    const txnCategory = useMemo(() => {
        const id = budget.key?.split('.')[1];
        return {
            id: id,
            emoji: budget.emoji ?? undefined,
        };
    }, [budget.emoji, budget.key]);
    const showEditModal = useEditBudgetsCategoryModal({
        onEdit: () => {
            onModalShown?.();
            setTimeout(() => {
                textInputMaskRef.current?.focus();
            }, 100);
        },
        onKeep: () => {
            textInputMaskRef.current?.blur?.();
        },
        category: budget,
    });
    const keyboardIsOpen = useGetIsKeyboardOpen();
    const handlePress = useCallback(() => {
        if (!modalShownRef?.current && budget.rollingAccumulatedLimit) {
            showEditModal();
            return;
        }
        // Used to reopen keyboard as the keyboard can be
        // closed on android with the input still focused
        if (isAndroid &&
            !keyboardIsOpen() &&
            textInputMaskRef.current?.isFocused()) {
            blur(textInputMaskRef);
        }
        focus(textInputMaskRef);
    }, [
        budget.rollingAccumulatedLimit,
        keyboardIsOpen,
        modalShownRef,
        showEditModal,
    ]);
    const handleEvent = useCallback((event) => {
        setIsFocused(event === 'focus');
    }, []);
    const handleFocus = useCallback(() => {
        handleEvent('focus');
        assureInputVisible(50);
        if (setOverParentErrorProp) {
            setOverParentErrorProp(isOverParentBudgetProp ??
                (subcategoriesTotalLimit ?? 0) > (budget.totalLimit ?? 0));
        }
    }, [
        assureInputVisible,
        budget.totalLimit,
        handleEvent,
        isOverParentBudgetProp,
        setOverParentErrorProp,
        subcategoriesTotalLimit,
    ]);
    const onBlur = useCallback(() => {
        handleEvent('blur');
        (setOverParentErrorProp ?? setOverParentError)(!!isOverParentBudget);
    }, [handleEvent, isOverParentBudget, setOverParentErrorProp]);
    const amountValue = useMemo(() => Number(amount.replace(/[^0-9.]/g, '')) || 0, [amount]);
    return (<>
      <TouchableHighlight onCard hoverStyle={{}} activeOpacity={1} onPress={handlePress} underlayColor={colors.background.underlay}>
        <View>
          <View style={styles.container}>
            <IconCircle width={44} height={44}>
              {budget.merchant && budget.iconUrl ? (<Logo R44 uri={budget.iconUrl} resizeMode="contain"/>) : (<IconTransactionCategory category={txnCategory}/>)}
            </IconCircle>
            <View style={styles.content}>
              <View style={styles.flex}>
                <Text Text-16>{budget.displayName}</Text>
                {!!renderAmountRow && <Spacer.H4 />}
                {renderAmountRow}
              </View>
              <View style={!isWeb && !budget.merchant && styles.inputContainer} 
    // When not focused, route taps through the row (handlePress)
    // instead of the TextInput: keeps the modal gate working for
    // rolled categories and keeps the input out of scroll gestures.
    pointerEvents={IsFocused ? 'auto' : 'box-only'}>
                <MoneyTextInput value={text} onBlur={onBlur} returnKeyType="done" onFocus={handleFocus} ref={textInputMaskRef} onChangeText={onChangeText} prefix={currency ?? ''} placeholder={`${currency}0`} underlineColorAndroid="transparent" maximumIntegerDigits={8} testID={`${budget.merchant ? budget.id : budget.categoryId}-currency-input`} style={[styles.textInput, IsFocused && styles.focusedStyles]}/>
              </View>
            </View>
          </View>
          <SubcategoryErrorText amount={amountValue} parentLimit={budget.totalLimit ?? 0} baseLimit={budget.baseLimit ?? 0} rolledAmount={budget.rollingAccumulatedLimit ?? 0} subCategoriesTotalLimit={subcategoriesTotalLimit} isParent={isParent} displayName={budget.displayName || ''} isFocused={IsFocused} isOverParentBudget={Boolean(
        // eslint-disable-next-line no-nested-ternary
        budget.parentCategoryId && IsFocused
            ? isOverParentBudget
            : IsFocused
                ? isOverParentBudget
                : overParentError)}/>
        </View>
      </TouchableHighlight>
      {!budget.parentCategoryId && !!subcategories?.length && (<>
          <LineSeparator noMargin/>
          <CollapsableContainer titleContent={<Text Text-16 Secondary>
                Subcategories
              </Text>} isCollapsedByDefault={false} hideBorder style={styles.collapsableContainer} onCollapsedChange={Keyboard.dismiss}>
            {subcategories.map((subcategory) => (<BudgetCategoryEditRow key={subcategory.key} budget={subcategory} isOverParentBudget={isOverParentBudget} setOverParentError={setOverParentError} onChange={onChange} hasAverages={hasAverages} withInitialFocus={initialFocusCategoryId === subcategory.categoryId} scrollRef={scrollRef} modalShownRef={modalShownRef} onModalShown={onModalShown}/>))}
          </CollapsableContainer>
        </>)}
    </>);
};
const TEXT_HEIGHT = rem(50);
const SubcategoryErrorText = ({ amount, parentLimit, baseLimit, rolledAmount, subCategoriesTotalLimit, isFocused, isOverParentBudget, isParent, displayName, }) => {
    const subsTotal = subCategoriesTotalLimit ?? 0;
    // Subs exceed the parent's effective limit (totalLimit already has the
    // rollover applied) but still fit the base budget — so a negative rollover is
    // the cause, not genuinely over-set subcategory budgets.
    const becauseOfRollover = rolledAmount < 0 && subsTotal > parentLimit && subsTotal < baseLimit;
    // Parent rows show whenever they're over; subcategory rows only while focused
    // and once their own amount has been entered.
    const show = becauseOfRollover ||
        (isOverParentBudget && (isParent || (isFocused && amount > 0)));
    const textHeight = useSharedValue(undefined);
    const styles = useAnimatedStyle(() => {
        if (textHeight.value === undefined && becauseOfRollover)
            return {};
        return {
            opacity: withTiming(show ? 1 : 0, {
                duration: show ? 400 : 200,
            }),
            // Not animated: on the New Architecture each frame of a height animation is
            // a shadow tree commit and layout pass on the UI thread, and this one runs
            // while the list scrolls the focused input above the keyboard.
            // eslint-disable-next-line no-nested-ternary
            height: show
                ? becauseOfRollover
                    ? textHeight.value ?? 0
                    : TEXT_HEIGHT
                : 0,
        };
    });
    const onLayout = useCallback((event) => {
        textHeight.value = event.nativeEvent.layout.height + rem(8);
    }, [textHeight]);
    if ((becauseOfRollover && (!isParent || !subsTotal)) ||
        (isParent && parentLimit > subsTotal)) {
        return null;
    }
    return (<Animated.View style={styles}>
      <View style={errorTextStyles.errorTextContainer}>
        {becauseOfRollover ? (<View onLayout={onLayout}>
            <Text TextThin-14 Negative>
              Your &apos;{displayName}&apos; budget is now less than the total
              of its subcategories because only parent budgets roll over, not
              subcategory budgets.
            </Text>
          </View>) : (<Text TextThin-14 Negative>
            {isParent
                ? `Heads up, your '${displayName}' budget is now less than the total of its subcategories`
                : 'Heads up, your subcategory totals are higher than the parent budget'}
          </Text>)}
      </View>
    </Animated.View>);
};
const errorTextStyles = StyleSheet.create({
    errorTextContainer: {
        position: 'absolute',
        left: isWeb ? rem(8) : rem(16),
        right: rem(16),
        bottom: rem(16),
    },
});
const styleSet = createStyleSheets((colors) => ({
    flex: {
        flex: 1,
    },
    container: {
        padding: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
        ...(isWeb && {
            paddingHorizontal: rem(8),
        }),
    },
    content: {
        flexDirection: 'row',
        flex: 1,
        alignItems: 'center',
        justifyContent: 'space-between',
        marginStart: rem(16),
        gap: rem(4),
    },
    textContainer: {
        flex: 1,
        marginRight: rem(8),
    },
    textInput: {
        fontFamily: 'Nunito-Bold',
        fontSize: rem(20),
        color: colors.text.primary,
        minWidth: rem(60),
        textAlign: 'right',
        ...(isWeb && {
            width: rem(150),
            height: rem(49),
            textAlign: 'right',
            borderWidth: rem(1),
            borderRadius: rem(8),
            paddingHorizontal: rem(16),
            caretColor: colors.text.selection,
            borderColor: colors.borders.divider,
            backgroundColor: colors.background.dark,
        }),
    },
    focusedStyles: {
        ...(isWeb && {
            borderColor: colors.elements.brand,
        }),
    },
    modalRenderItem: {
        backgroundColor: colors.background.dark,
        borderRadius: rem(16),
    },
    inputContainer: {
        flexShrink: 0,
        alignItems: 'flex-end',
        overflow: 'hidden',
    },
    collapsableContainer: {
        ...(isWeb && {
            margin: -rem(8),
        }),
    },
}));
export default memo(BudgetCategoryEditRow);
